import { useEffect, useState } from 'react';
import { Button, Space, Form, Input, Select, App, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import { withDevModalFilePath, NumericInput, PhoneInput, PostalCodeInput, EmailInput } from '../../../components/common';
import { katakanaRule, phoneNumberRule, postalCodeRule } from '@/constants/validation';
import { getMutationPendingState } from '../../../utils/trpcMutation';

const { Modal } = withDevModalFilePath('client/src/pages/admin/companies/CompanyBranchesModal.tsx');
const { Option } = Select;

interface Branch {
  id: string;
  companyId: string;
  displayId: string;
  name: string;
  nameKana?: string;
  type?: string;
  phone?: string;
  email?: string;
  managerName?: string;
  employeeCount?: number;
  isActive: boolean;
  postalCode?: string;
  prefecture?: string;
  city?: string;
  address?: string;
  building?: string;
  fax?: string;
  managerEmail?: string;
}

const normalizeBranch = (branch: Partial<Branch> | null | undefined): Branch | null => {
  if (!branch?.id || !branch.companyId || !branch.name) {
    return null;
  }

  return {
    id: branch.id,
    companyId: branch.companyId,
    displayId: branch.displayId ?? '',
    name: branch.name,
    nameKana: branch.nameKana,
    type: branch.type,
    phone: branch.phone,
    email: branch.email,
    managerName: branch.managerName,
    employeeCount: branch.employeeCount,
    isActive: branch.isActive ?? true,
    postalCode: branch.postalCode,
    prefecture: branch.prefecture,
    city: branch.city,
    address: branch.address,
    building: branch.building,
    fax: branch.fax,
    managerEmail: branch.managerEmail,
  };
};

interface CompanyBranchesModalProps {
  open: boolean;
  company: { id: string; name: string };
  onClose: () => void;
}

type BranchFormValues = Partial<Branch>;

export const CompanyBranchesModal: React.FC<CompanyBranchesModalProps> = ({
  open,
  company,
  onClose
}) => {
  const { message } = App.useApp();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [form] = Form.useForm<BranchFormValues>();

  useEffect(() => {
    if (!isFormVisible) {
      return;
    }

    if (editingBranch) {
      form.setFieldsValue(editingBranch);
    } else {
      form.resetFields();
    }
  }, [form, isFormVisible, editingBranch]);

  // 支店一覧取得
  const { data: branches, refetch } = trpc.company.branches.list.useQuery(
    { companyId: company.id },
    { enabled: open }
  );
  const normalizedBranches = (branches ?? [])
    .map((branch) => normalizeBranch(branch))
    .filter((branch): branch is Branch => branch !== null);

  // 支店作成
  const createMutation = trpc.company.branches.create.useMutation({
    onSuccess: () => {
      message.success('支店を作成しました');
      form.resetFields();
      setIsFormVisible(false);
      refetch();
    },
    onError: (error) => {
      message.error(`作成に失敗しました: ${error.message}`);
    }
  });

  // 支店更新
  const updateMutation = trpc.company.branches.update.useMutation({
    onSuccess: () => {
      message.success('支店情報を更新しました');
      form.resetFields();
      setIsFormVisible(false);
      setEditingBranch(null);
      refetch();
    },
    onError: (error) => {
      message.error(`更新に失敗しました: ${error.message}`);
    }
  });

  // 支店削除
  const deleteMutation = trpc.company.branches.delete.useMutation({
    onSuccess: () => {
      message.success('支店を削除しました');
      refetch();
    },
    onError: (error) => {
      message.error(`削除に失敗しました: ${error.message}`);
    }
  });

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setIsFormVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = (values: BranchFormValues) => {
    if (editingBranch) {
      updateMutation.mutate({
        id: editingBranch.id,
        data: values
      });
    } else {
      createMutation.mutate({
        ...values,
        companyId: company.id
      });
    }
  };

  const columns: ResponsiveColumnType<Branch>[] = [
    {
      title: '支店ID',
      dataIndex: 'displayId',
      key: 'displayId',
      width: 100,
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '支店名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.nameKana && <div style={{ fontSize: '12px', color: '#999' }}>{record.nameKana}</div>}
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '種別',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      mobileRole: 'tag',
      mobilePriority: 3,
    },
    {
      title: '責任者',
      dataIndex: 'managerName',
      key: 'managerName',
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: '連絡先',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.email && <div style={{ fontSize: '12px' }}>{record.email}</div>}
          {record.phone && <div style={{ fontSize: '12px' }}>{record.phone}</div>}
        </div>
      ),
      mobileRole: 'description',
      mobilePriority: 5,
    },
    {
      title: '従業員数',
      dataIndex: 'employeeCount',
      key: 'employeeCount',
      width: 100,
      align: 'right',
      render: (count) => count ? `${count}名` : '-',
      mobileRole: 'hidden',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="削除確認"
            description="この支店を削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
      mobileRole: 'hidden',
    }
  ];

  return (
    <Modal
      title={`${company.name} - 支店管理`}
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          閉じる
        </Button>,
        <Button
          key="add"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingBranch(null);
            setIsFormVisible(true);
          }}
        >
          新規支店登録
        </Button>
      ]}
    >
      <ResponsiveTable
        columns={columns}
        dataSource={normalizedBranches}
        rowKey="id"
        pagination={false}
        size="small"
        mobileCardConfig={{
          titleField: 'name',
          actions: (record) => (
            <Space size="small">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
              >
                編集
              </Button>
              <Popconfirm
                title="削除確認"
                description="この支店を削除してもよろしいですか？"
                onConfirm={() => handleDelete(record.id)}
                okText="削除"
                cancelText="キャンセル"
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                >
                  削除
                </Button>
              </Popconfirm>
            </Space>
          ),
        }}
      />

      <Modal
        title={editingBranch ? '支店情報編集' : '新規支店登録'}
        open={isFormVisible}
        onCancel={() => {
          form.resetFields();
          setIsFormVisible(false);
          setEditingBranch(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={getMutationPendingState(createMutation) || getMutationPendingState(updateMutation)}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >          <Form.Item label="支店ID">
            <Input value={editingBranch ? editingBranch.displayId : '保存後に自動採番されます'} disabled />
          </Form.Item>

          <Form.Item
            name="name"
            label="支店名"
            rules={[
              { required: true, message: '支店名を入力してください' },
              { max: 200, message: '200文字以内で入力してください' }
            ]}
          >
            <Input placeholder="東京支店" />
          </Form.Item>

          <Form.Item
            name="nameKana"
            label="支店名カナ"
            rules={[
              { max: 200, message: '200文字以内で入力してください' },
              katakanaRule(),
            ]}
          >
            <Input placeholder="トウキョウシテン" />
          </Form.Item>

          <Form.Item
            name="type"
            label="種別"
          >
            <Select placeholder="選択してください">
              <Option value="本社">本社</Option>
              <Option value="支店">支店</Option>
              <Option value="営業所">営業所</Option>
              <Option value="工場">工場</Option>
              <Option value="研究所">研究所</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="postalCode"
            label="郵便番号"
            rules={[postalCodeRule()]}
          >
            <PostalCodeInput placeholder="123-4567" />
          </Form.Item>

          <Form.Item
            name="prefecture"
            label="都道府県"
          >
            <Input placeholder="東京都" />
          </Form.Item>

          <Form.Item
            name="city"
            label="市区町村"
          >
            <Input placeholder="千代田区" />
          </Form.Item>

          <Form.Item
            name="address"
            label="住所"
          >
            <Input placeholder="丸の内1-1-1" />
          </Form.Item>

          <Form.Item
            name="building"
            label="建物名"
          >
            <Input placeholder="サンプルビル 10F" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="電話番号"
            rules={[
              { max: 20, message: '電話番号は20文字以内で入力してください' },
              phoneNumberRule(),
            ]}
          >
            <PhoneInput placeholder="03-1234-5678" />
          </Form.Item>

          <Form.Item
            name="fax"
            label="FAX番号"
            rules={[
              { max: 20, message: 'FAX番号は20文字以内で入力してください' },
              phoneNumberRule('有効なFAX番号を入力してください'),
            ]}
          >
            <PhoneInput placeholder="03-1234-5679" />
          </Form.Item>

          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { type: 'email', message: '正しいメールアドレスを入力してください' }
            ]}
          >
            <EmailInput placeholder="branch@example.com" />
          </Form.Item>

          <Form.Item
            name="managerName"
            label="責任者名"
          >
            <Input placeholder="佐藤 花子" />
          </Form.Item>

          <Form.Item
            name="managerEmail"
            label="責任者メール"
            rules={[
              { type: 'email', message: '正しいメールアドレスを入力してください' }
            ]}
          >
            <EmailInput placeholder="manager@example.com" />
          </Form.Item>

          <Form.Item
            name="employeeCount"
            label="従業員数"
          >
            <NumericInput style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};
